/**
 * useAvailableRewards — reads claimable fee balance from FeeLocker via clanker-sdk.
 *
 * Direct RPC read to the on-chain FeeLocker contract.
 * Returns the raw bigint amount plus a formatted string with native currency symbol.
 *
 * Story 6.3 — Task 1
 */

import { useQuery } from "@tanstack/react-query";
import { formatUnits } from "viem";
import { getPublicClient } from "wagmi/actions";

import { getChainAddresses } from "../../lib/chain-addresses";
import { getActiveChain, getNativeCurrencySymbol, wagmiConfig } from "../../lib/wagmi-config";

function isHexAddress(value: string): value is `0x${string}` {
	return /^0x[a-fA-F0-9]{40}$/.test(value);
}

type UseAvailableRewardsParams = {
	tokenAddress: string | undefined;
	rewardRecipient: string | undefined;
};

export function useAvailableRewards({ tokenAddress, rewardRecipient }: UseAvailableRewardsParams) {
	return useQuery({
		queryKey: ["chain", "available-rewards", tokenAddress, rewardRecipient] as const,
		queryFn: async () => {
			if (!tokenAddress || !rewardRecipient) {
				// eslint-disable-next-line no-restricted-syntax -- TanStack Query requires thrown errors
				throw new Error("Missing token address or reward recipient");
			}

			if (!isHexAddress(tokenAddress) || !isHexAddress(rewardRecipient)) {
				// eslint-disable-next-line no-restricted-syntax -- TanStack Query requires thrown errors
				throw new Error("Invalid address format");
			}

			const activeChain = getActiveChain();
			const publicClient = getPublicClient(wagmiConfig, { chainId: activeChain.id });
			if (!publicClient) {
				// eslint-disable-next-line no-restricted-syntax -- TanStack Query requires thrown errors
				throw new Error("Network connection unavailable");
			}

			const chainAddresses = getChainAddresses(activeChain.id);
			if (!chainAddresses) {
				// eslint-disable-next-line no-restricted-syntax -- TanStack Query requires thrown errors
				throw new Error(`Unsupported chain: ${activeChain.id}`);
			}

			const { Clanker } = await import("@repo/clanker-sdk/v4");
			const clanker = new Clanker({
				publicClient,
			});

			// FeeLocker.availableFees(rewardRecipient, token) — token is the fee currency (WETH),
			// NOT the ERC-20 token address. Fees are collected in the pool's base currency.
			const amount = await clanker.availableRewards({
				token: chainAddresses.weth,
				rewardRecipient: rewardRecipient,
			});

			const symbol = getNativeCurrencySymbol();
			const formatted = `${formatUnits(amount, 18)} ${symbol}`;

			return { amount, formatted };
		},
		enabled: !!tokenAddress && !!rewardRecipient,
		staleTime: 30_000,
		refetchInterval: 30_000,
	});
}
